import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Box, Button,
  Chip, IconButton, Paper, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  List, ListItem, ListItemIcon, ListItemText, Divider, Alert,
  CircularProgress, Tabs, Tab, CardActions, Avatar, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails, Fab, Tooltip,
  Stepper, Step, StepLabel
} from '@mui/material';
import {
  Assignment, LocationOn, Schedule, Star, TrendingUp, AttachMoney,
  CheckCircle, AccessTime, Navigation, Phone, Email, PhotoCamera,
  Videocam, Build, FilterList, Search, PlayArrow, Pause, Stop,
  CheckBox, Cancel, Warning, Info, Verified, Edit, Add, Route,
  MyLocation, Timer, CameraAlt, Upload, FileDownload, ExpandMore,
  DirectionsCar, Speed, NearMe, Engineering, ElectricalServices,
  LocalFireDepartment, Plumbing, Security, RuleFolder, Assessment, Construction
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import DialogCloseButton from '../components/common/DialogCloseButton';
import inspectorService from '../services/inspectorService';

const InspectorInspectionsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [inspectionDetailsDialog, setInspectionDetailsDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [startInspectionDialog, setStartInspectionDialog] = useState(false);
  const [filterDialog, setFilterDialog] = useState(false);
  const [checklistDialog, setChecklistDialog] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Data states
  const [inspections, setInspections] = useState([]);
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [stats, setStats] = useState({});

  // Sample comprehensive inspection data
  const sampleInspectionData = [
    {
      id: 'INSP-001',
      projectId: 'PROJ-2001',
      projectTitle: 'Commercial Office Complex - Phase 2',
      inspectionType: 'structural',
      phase: 'Foundation & Framework Inspection',
      client: 'Corporate Developments Ltd',
      contractor: 'Premier Construction Group',
      location: {
        address: '250 Nelson Mandela Ave, Harare',
        coordinates: { lat: -17.8292, lng: 31.0522 },
        distance: 15.2,
        area: 'CBD'
      },
      scheduledDate: '2025-02-15',
      scheduledTime: '09:30',
      duration: 180,
      priority: 'high',
      status: 'in_progress',
      paymentAmount: 350,
      riskLevel: 'medium',
      description: 'Comprehensive structural integrity inspection of concrete foundation and steel framework for 12-story office building',
      requirements: [
        'Foundation depth and strength verification',
        'Concrete compression test results review',
        'Steel framework alignment and welding quality assessment',
        'Load-bearing calculations verification',
        'Safety compliance assessment and documentation'
      ],
      checklist: [
        { item: 'Foundation depth measurement (min 2.5m)', status: 'completed', critical: true, notes: 'Measured 2.8m - compliant' },
        { item: 'Concrete strength verification (min 25MPa)', status: 'completed', critical: true, notes: '28MPa achieved - passed' },
        { item: 'Reinforcement placement check', status: 'in_progress', critical: true, notes: 'Currently inspecting rebar spacing' },
        { item: 'Steel framework alignment tolerance', status: 'pending', critical: false, notes: '' },
        { item: 'Welding quality assessment', status: 'pending', critical: true, notes: '' },
        { item: 'Safety compliance review', status: 'pending', critical: false, notes: '' }
      ],
      documents: ['Structural drawings Rev.3', 'Engineering calculations', 'Material test certificates', 'Safety protocols'],
      clientContact: '+263771234567',
      contractorContact: '+263771234568',
      estimatedCompletion: '2025-02-15 12:30',
      photos: 8,
      findings: 2,
      complianceScore: 85
    },
    {
      id: 'INSP-002',
      projectId: 'PROJ-2002',
      projectTitle: 'Shopping Mall Electrical Systems',
      inspectionType: 'electrical',
      phase: 'Final Electrical Installation Inspection',
      client: 'Retail Development Corporation',
      contractor: 'ElectroTech Solutions',
      location: {
        address: '89 Borrowdale Road, Harare',
        coordinates: { lat: -17.8175, lng: 31.0467 },
        distance: 8.7,
        area: 'Borrowdale'
      },
      scheduledDate: '2025-02-15',
      scheduledTime: '14:00',
      duration: 150,
      priority: 'urgent',
      status: 'assigned',
      paymentAmount: 280,
      riskLevel: 'high',
      description: 'Final electrical systems inspection before commissioning for large retail complex with multiple tenants',
      requirements: [
        'Main electrical panel configuration verification',
        'Emergency lighting systems functionality test',
        'Fire alarm integration verification',
        'Power distribution verification across all zones',
        'Grounding system compliance check'
      ],
      checklist: [
        { item: 'Main panel configuration verification', status: 'pending', critical: true, notes: '' },
        { item: 'Circuit breaker ratings compliance', status: 'pending', critical: true, notes: '' },
        { item: 'Emergency lighting test (90min backup)', status: 'pending', critical: true, notes: '' },
        { item: 'Fire alarm systems integration', status: 'pending', critical: true, notes: '' },
        { item: 'Grounding verification (<5 ohms)', status: 'pending', critical: true, notes: '' },
        { item: 'Load testing all circuits', status: 'pending', critical: false, notes: '' }
      ],
      documents: ['Electrical schematics Rev.2', 'Panel schedules', 'Test certificates', 'Fire alarm drawings'],
      clientContact: '+263771234569',
      contractorContact: '+263771234570',
      estimatedCompletion: '2025-02-15 16:30',
      photos: 0,
      findings: 0,
      complianceScore: 0
    },
    {
      id: 'INSP-003',
      projectId: 'PROJ-2003',
      projectTitle: 'Residential Complex Fire Safety',
      inspectionType: 'fire_safety',
      phase: 'Fire Safety Systems Final Inspection',
      client: 'Housing Development Co',
      contractor: 'Fire Protection Systems Ltd',
      location: {
        address: '45 Westgate Drive, Harare',
        coordinates: { lat: -17.7990, lng: 31.0194 },
        distance: 12.5,
        area: 'Westgate'
      },
      scheduledDate: '2025-02-16',
      scheduledTime: '10:00',
      duration: 120,
      priority: 'normal',
      status: 'assigned',
      paymentAmount: 220,
      riskLevel: 'medium',
      description: 'Comprehensive fire safety inspection for 240-unit residential complex including sprinkler systems and emergency exits',
      requirements: [
        'Sprinkler system pressure test and coverage verification',
        'Emergency exit routes and signage compliance',
        'Fire alarm system functionality test',
        'Fire-rated door and window installations',
        'Smoke detection system verification'
      ],
      checklist: [
        { item: 'Sprinkler system pressure test', status: 'pending', critical: true, notes: '' },
        { item: 'Emergency exit route compliance', status: 'pending', critical: true, notes: '' },
        { item: 'Fire alarm functionality test', status: 'pending', critical: true, notes: '' },
        { item: 'Fire-rated installations check', status: 'pending', critical: false, notes: '' },
        { item: 'Smoke detection coverage', status: 'pending', critical: true, notes: '' }
      ],
      documents: ['Fire safety plans', 'Sprinkler drawings', 'Emergency evacuation plans'],
      clientContact: '+263771234571',
      contractorContact: '+263771234572',
      estimatedCompletion: '2025-02-16 12:00',
      photos: 0,
      findings: 0,
      complianceScore: 0
    },
    {
      id: 'INSP-004',
      projectId: 'PROJ-2004',
      projectTitle: 'Industrial Warehouse Structural Review',
      inspectionType: 'structural',
      phase: 'Steel Framework and Roofing Inspection',
      client: 'Industrial Holdings Ltd',
      contractor: 'Heavy Construction Inc',
      location: {
        address: '789 Msasa Industrial Road, Harare',
        coordinates: { lat: -17.8400, lng: 31.1200 },
        distance: 25.3,
        area: 'Msasa'
      },
      scheduledDate: '2025-02-17',
      scheduledTime: '08:00',
      duration: 240,
      priority: 'high',
      status: 'completed',
      paymentAmount: 400,
      riskLevel: 'low',
      description: 'Structural inspection of large industrial warehouse with 30m span steel framework and heavy-duty roofing system',
      requirements: [
        'Steel framework load-bearing verification',
        'Welding quality and joint integrity assessment',
        'Roofing system attachment and waterproofing',
        'Foundation settlement monitoring',
        'Crane rail installation verification'
      ],
      checklist: [
        { item: 'Steel framework load verification', status: 'completed', critical: true, notes: 'All connections meet specifications' },
        { item: 'Welding quality assessment', status: 'completed', critical: true, notes: 'NDT testing passed' },
        { item: 'Roofing system integrity', status: 'completed', critical: false, notes: 'Waterproofing excellent' },
        { item: 'Foundation settlement check', status: 'completed', critical: true, notes: 'Within tolerance limits' },
        { item: 'Crane rail installation', status: 'completed', critical: false, notes: 'Alignment perfect' }
      ],
      documents: ['Structural calculations', 'Welding certificates', 'NDT reports', 'Foundation survey'],
      clientContact: '+263771234573',
      contractorContact: '+263771234574',
      estimatedCompletion: '2025-02-17 12:00',
      photos: 15,
      findings: 1,
      complianceScore: 98
    }
  ];

  // Load inspections data from API
  useEffect(() => {
    const loadInspections = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await inspectorService.getMyInspections();
        const data = response?.data || [];

        // Map API response to component shape (fill defaults for fields the API may not return)
        const mapped = data.map(item => ({
          id: item.id,
          projectId: item.projectId || item.project_id,
          projectTitle: item.projectTitle || item.project_title || 'Untitled Project',
          inspectionType: item.inspectionType || item.inspection_type || 'general',
          phase: item.phase || item.description || '',
          client: item.client || 'Unknown Client',
          clientId: item.clientId || item.client_id,
          contractor: item.contractor || 'Unknown Contractor',
          contractorId: item.contractorId || item.contractor_id,
          location: item.location || { address: '', coordinates: {}, distance: 0, area: '' },
          scheduledDate: item.scheduledDate || item.scheduled_date || item.createdAt || '',
          scheduledTime: item.scheduledTime || item.scheduled_time || '',
          duration: item.duration || 0,
          priority: item.priority || 'normal',
          status: item.status || 'pending',
          paymentAmount: Number(item.paymentAmount || item.payment_amount || item.agreed_fee || 0),
          riskLevel: item.riskLevel || item.risk_level || 'low',
          description: item.description || item.assignment_notes || '',
          requirements: item.requirements || [],
          checklist: item.checklist || [],
          photos: item.photos || [],
          findings: item.findings || [],
          complianceScore: Number(item.complianceScore || item.compliance_score || 0),
          milestoneId: item.milestoneId || item.milestone_id,
          createdAt: item.createdAt || item.created_at,
          completedAt: item.completedAt || item.completed_at,
        }));

        setInspections(mapped);
        setFilteredInspections(mapped);

        // Calculate stats
        setStats({
          total: mapped.length,
          assigned: mapped.filter(i => i.status === 'pending' || i.status === 'accepted').length,
          inProgress: mapped.filter(i => i.status === 'in_progress').length,
          completed: mapped.filter(i => i.status === 'completed').length,
          highPriority: mapped.filter(i => i.priority === 'high' || i.priority === 'urgent').length,
          totalValue: mapped.reduce((sum, i) => sum + i.paymentAmount, 0),
          avgCompliance: mapped.filter(i => i.complianceScore > 0).length > 0
            ? Math.round(mapped.filter(i => i.complianceScore > 0).reduce((sum, i) => sum + i.complianceScore, 0) / mapped.filter(i => i.complianceScore > 0).length)
            : 0
        });
      } catch (err) {
        console.error('Error loading inspections:', err);
        setError('Failed to load inspections. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      loadInspections();
    }
  }, [user?.id]);

  // Filter inspections
  useEffect(() => {
    let filtered = inspections;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(i => i.inspectionType === typeFilter);
    }
    
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(i => i.priority === priorityFilter);
    }
    
    if (locationFilter !== 'all') {
      filtered = filtered.filter(i => i.location.area === locationFilter);
    }
    
    setFilteredInspections(filtered);
  }, [inspections, statusFilter, typeFilter, priorityFilter, locationFilter]);

  const getStatusColor = (status) => {
    const colors = {
      'assigned': 'primary',
      'in_progress': 'warning',
      'completed': 'success',
      'pending': 'info',
      'failed': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'error',
      'high': 'warning',
      'normal': 'primary',
      'low': 'info'
    };
    return colors[priority] || 'default';
  };

  const getRiskLevelColor = (risk) => {
    const colors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'success'
    };
    return colors[risk] || 'default';
  };

  const getInspectionTypeIcon = (type) => {
    const icons = {
      'structural': <Engineering />,
      'electrical': <ElectricalServices />,
      'fire_safety': <LocalFireDepartment />,
      'plumbing': <Plumbing />,
      'accessibility': <Assignment />,
      'general': <Construction />
    };
    return icons[type] || <Assignment />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleAcceptInspection = async (inspection) => {
    try {
      setError('');
      await inspectorService.startInspection(inspection.id); // accept uses the same start endpoint
      setSuccess('Inspection accepted successfully');
      // Refresh data
      const response = await inspectorService.getMyInspections();
      const data = response?.data || [];
      setInspections(data);
    } catch (err) {
      setError('Failed to accept inspection: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleStartInspection = (inspection) => {
    setSelectedInspection(inspection);
    setStartInspectionDialog(true);
  };

  const handleConfirmStartInspection = async () => {
    if (!selectedInspection) return;
    try {
      setError('');
      await inspectorService.startInspection(selectedInspection.id);
      setSuccess('Inspection started successfully');
      setStartInspectionDialog(false);
      // Refresh data
      const response = await inspectorService.getMyInspections();
      const data = response?.data || [];
      setInspections(data);
    } catch (err) {
      setError('Failed to start inspection: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCompleteInspection = async (inspection) => {
    try {
      setError('');
      await inspectorService.completeInspection(inspection.id, {
        findings: inspection.findings || [],
        complianceScore: inspection.complianceScore || 0,
      });
      setSuccess('Inspection completed successfully');
      // Refresh data
      const response = await inspectorService.getMyInspections();
      const data = response?.data || [];
      setInspections(data);
    } catch (err) {
      setError('Failed to complete inspection: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleViewInspectionDetails = (inspection) => {
    setSelectedInspection(inspection);
    setInspectionDetailsDialog(true);
  };

  const handleViewChecklist = (inspection) => {
    setSelectedInspection(inspection);
    setChecklistDialog(true);
  };

  const calculateChecklistProgress = (checklist) => {
    if (!checklist || checklist.length === 0) return 0;
    const completed = checklist.filter(item => item.status === 'completed').length;
    return (completed / checklist.length) * 100;
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setPriorityFilter('all');
    setLocationFilter('all');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading inspections...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Quality Inspections
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage quality inspections, compliance checks, and certification processes
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterDialog(true)}
          >
            Filters
          </Button>
          <Button variant="outlined" startIcon={<Route />}>
            Optimize Route
          </Button>
        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="warning.main">
                {stats.assigned}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Assigned
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="info.main">
                {stats.inProgress}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="error.main">
                {stats.highPriority}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High Priority
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">
                {stats.avgCompliance}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Compliance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {(statusFilter !== 'all' || typeFilter !== 'all' || priorityFilter !== 'all' || locationFilter !== 'all') && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">Active Filters:</Typography>
              {statusFilter !== 'all' && (
                <Chip 
                  label={`Status: ${statusFilter}`} 
                  onDelete={() => setStatusFilter('all')}
                  color="primary"
                  size="small"
                />
              )}
              {typeFilter !== 'all' && (
                <Chip 
                  label={`Type: ${typeFilter.replace('_', ' ')}`} 
                  onDelete={() => setTypeFilter('all')}
                  color="primary"
                  size="small"
                />
              )}
              {priorityFilter !== 'all' && (
                <Chip 
                  label={`Priority: ${priorityFilter}`} 
                  onDelete={() => setPriorityFilter('all')}
                  color="primary"
                  size="small"
                />
              )}
              {locationFilter !== 'all' && (
                <Chip 
                  label={`Location: ${locationFilter}`} 
                  onDelete={() => setLocationFilter('all')}
                  color="primary"
                  size="small"
                />
              )}
              <Button size="small" onClick={clearFilters}>
                Clear All
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Inspections List */}
      <Grid container spacing={3}>
        {filteredInspections.map((inspection) => (
          <Grid item xs={12} key={inspection.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getInspectionTypeIcon(inspection.inspectionType)}
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {inspection.inspectionType.replace('_', ' ').toUpperCase()} Inspection
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {inspection.projectTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {inspection.phase}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={inspection.status.replace('_', ' ').toUpperCase()} 
                      color={getStatusColor(inspection.status)}
                      size="small"
                    />
                    <Chip 
                      label={inspection.priority.toUpperCase()} 
                      color={getPriorityColor(inspection.priority)}
                      size="small"
                    />
                    <Chip 
                      label={`${inspection.riskLevel} risk`.toUpperCase()} 
                      color={getRiskLevelColor(inspection.riskLevel)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                  {inspection.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Key Information Grid */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Client</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {inspection.client}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Contractor</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {inspection.contractor}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(inspection.scheduledDate)} at {inspection.scheduledTime}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Payment</Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                      {formatCurrency(inspection.paymentAmount)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Progress and Location Info */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Checklist Progress: {Math.round(calculateChecklistProgress(inspection.checklist))}%
                    </Typography>
                    {inspection.complianceScore > 0 && (
                      <Typography variant="subtitle2" color="success.main">
                        Compliance: {inspection.complianceScore}%
                      </Typography>
                    )}
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={calculateChecklistProgress(inspection.checklist)} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">
                      {inspection.location.area} • {inspection.location.distance}km
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Timer fontSize="small" color="action" />
                    <Typography variant="body2">
                      {inspection.duration} mins
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhotoCamera fontSize="small" color="action" />
                    <Typography variant="body2">
                      {inspection.photos} photos
                    </Typography>
                  </Box>
                  {inspection.findings > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Warning fontSize="small" color="warning" />
                      <Typography variant="body2" color="warning.main">
                        {inspection.findings} finding{inspection.findings > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
              
              <CardActions sx={{ pt: 0 }}>
                <Button 
                  size="small"
                  onClick={() => handleViewInspectionDetails(inspection)}
                >
                  View Details
                </Button>
                <Button 
                  size="small" 
                  startIcon={<RuleFolder />}
                  onClick={() => handleViewChecklist(inspection)}
                >
                  Checklist
                </Button>
                <Button size="small" startIcon={<Route />}>
                  Directions
                </Button>
                <Button size="small" startIcon={<Phone />}>
                  Contact
                </Button>
                {inspection.status === 'assigned' && (
                  <Button 
                    size="small" 
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => handleStartInspection(inspection)}
                  >
                    Start
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Results */}
      {filteredInspections.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No inspections found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or check back later for new assignments.
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={clearFilters}>
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Inspection Details Dialog */}
      <Dialog
        open={inspectionDetailsDialog}
        onClose={() => setInspectionDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setInspectionDetailsDialog(false)} />
        <DialogTitle>Inspection Details</DialogTitle>
        <DialogContent dividers>
          {selectedInspection && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedInspection.inspectionType.replace('_', ' ').toUpperCase()} Inspection
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedInspection.projectTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedInspection.description}
              </Typography>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Requirements</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedInspection.requirements?.map((req, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckBox />
                        </ListItemIcon>
                        <ListItemText primary={req} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Location & Schedule</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    📍 <strong>Address:</strong> {selectedInspection.location?.address}<br />
                    🕐 <strong>Date & Time:</strong> {selectedInspection.scheduledDate} at {selectedInspection.scheduledTime}<br />
                    ⏱️ <strong>Duration:</strong> {selectedInspection.duration} minutes<br />
                    📱 <strong>Client Contact:</strong> {selectedInspection.clientContact}<br />
                    📱 <strong>Contractor Contact:</strong> {selectedInspection.contractorContact}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">Documents & Resources</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedInspection.documents?.map((doc, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <FileDownload />
                        </ListItemIcon>
                        <ListItemText primary={doc} />
                        <Button size="small" startIcon={<FileDownload />}>
                          Download
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectionDetailsDialog(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Route />}>
            Get Directions
          </Button>
          <Button variant="contained" startIcon={<PlayArrow />}>
            Start Inspection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Checklist Dialog */}
      <Dialog
        open={checklistDialog}
        onClose={() => setChecklistDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setChecklistDialog(false)} />
        <DialogTitle>Inspection Checklist</DialogTitle>
        <DialogContent dividers>
          {selectedInspection && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedInspection.inspectionType.replace('_', ' ').toUpperCase()} - {selectedInspection.projectTitle}
              </Typography>
              
              <List>
                {selectedInspection.checklist?.map((item, index) => (
                  <ListItem key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <ListItemIcon>
                      {item.critical ? <Warning color="error" /> : <CheckBox />}
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: item.critical ? 600 : 400 }}>
                            {item.item}
                          </Typography>
                          <Chip 
                            label={item.status.toUpperCase()} 
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {item.critical && (
                            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                              CRITICAL ITEM
                            </Typography>
                          )}
                          {item.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Notes: {item.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChecklistDialog(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Edit />}>
            Update Progress
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Inspection Dialog */}
      <Dialog
        open={startInspectionDialog}
        onClose={() => setStartInspectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setStartInspectionDialog(false)} />
        <DialogTitle>Start Quality Inspection</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ready to start inspection for:
          </Typography>
          <Typography variant="h6" gutterBottom>
            {selectedInspection?.inspectionType?.replace('_', ' ').toUpperCase()} Inspection
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedInspection?.projectTitle}
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Ensure you have all required equipment, documentation, and safety gear before starting.
          </Alert>
          
          <Alert severity="warning" sx={{ mt: 1 }}>
            Location: {selectedInspection?.location?.address}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartInspectionDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PlayArrow />} onClick={handleConfirmStartInspection}>
            Start Inspection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialog}
        onClose={() => setFilterDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setFilterDialog(false)} />
        <DialogTitle>Filter Inspections</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Inspection Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Inspection Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="structural">Structural</MenuItem>
                  <MenuItem value="electrical">Electrical</MenuItem>
                  <MenuItem value="fire_safety">Fire Safety</MenuItem>
                  <MenuItem value="plumbing">Plumbing</MenuItem>
                  <MenuItem value="accessibility">Accessibility</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High Priority</MenuItem>
                  <MenuItem value="normal">Normal Priority</MenuItem>
                  <MenuItem value="low">Low Priority</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  label="Location"
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  <MenuItem value="CBD">CBD</MenuItem>
                  <MenuItem value="Borrowdale">Borrowdale</MenuItem>
                  <MenuItem value="Westgate">Westgate</MenuItem>
                  <MenuItem value="Msasa">Msasa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setFilterDialog(false)}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleStartInspection(filteredInspections.find(i => i.status === 'assigned'))}
      >
        <CameraAlt />
      </Fab>
    </Container>
  );
};

export default InspectorInspectionsPage; 